import { cicBucket } from "./fundParseCicClassification";
import {
    FundParseExposureChartsSummary,
    FundParseExposureDimensionKey,
    FundParseExposureDimensionSummary,
    FundParseExposureSlice
} from "./fundParseTypes";
import { TptRow } from "./fundParseTptTypes";

const MARKET_VALUE_KEY = "24_Market_valuation_in_portfolio_currency_(B)";
const CIC_COLUMN = "12_CIC_code_of_the_instrument";
const ISSUER_COUNTRY_COLUMN = "52_Issuer_country";
const QUOTATION_CURRENCY_COLUMN = "21_Quotation_currency_(A)";
const PORTFOLIO_CURRENCY_COLUMN = "4_Portfolio_currency_(B)";
const CREDIT_QUALITY_STEP_COLUMN = "59_Credit_quality_step";

const LOOKTHROUGH_BUCKETS = new Set(["bond", "equity", "cash"]);
const EXCLUDED_BUCKETS = new Set(["derivative", "unknown"]);
const COUNTRY_TOP_N = 10;

const CIC_BUCKET_LABELS: Record<string, string> = {
    bond: "Bond",
    equity: "Equity",
    cash: "Cash"
};

function toFloat(value: unknown): number {
    if (value === null || value === "" || typeof value === "undefined") {
        return 0;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
}

function normalizeKey(value: unknown): string {
    const text = String(value ?? "").trim();
    if (!text || ["NAN", "NONE", "NAT"].includes(text.toUpperCase())) {
        return "";
    }
    return text;
}

function buildSlice(key: string, label: string, netValue: number, rowCount: number): FundParseExposureSlice {
    return {
        key,
        label,
        netValue,
        rowCount,
        chartValue: Math.abs(netValue),
        isNegative: netValue < 0
    };
}

function aggregateByKey(frame: PreparedRow[], keyColumn: string, labelFormatter?: (key: string) => string): FundParseExposureSlice[] {
    const grouped = new Map<string, { netValue: number; rowCount: number }>();
    for (const row of frame) {
        const rawKey = normalizeKey(row[keyColumn]);
        const groupKey = rawKey || "__blank__";
        const current = grouped.get(groupKey) || { netValue: 0, rowCount: 0 };
        current.netValue += row.marketValue;
        current.rowCount += 1;
        grouped.set(groupKey, current);
    }

    const slices = Array.from(grouped.entries()).map(([key, stats]) => {
        const label = key === "__blank__" ? "Not reported" : labelFormatter ? labelFormatter(key) : key;
        return buildSlice(key, label, stats.netValue, stats.rowCount);
    });
    slices.sort((left, right) => right.chartValue - left.chartValue);
    return slices;
}

function collapseTopN(slices: FundParseExposureSlice[], topN: number): [FundParseExposureSlice[], FundParseExposureSlice | null] {
    if (slices.length <= topN) {
        return [slices, null];
    }
    const head = slices.slice(0, topN);
    const tail = slices.slice(topN);
    const otherNet = tail.reduce((sum, item) => sum + item.netValue, 0);
    const otherChart = tail.reduce((sum, item) => sum + item.chartValue, 0);
    const otherRows = tail.reduce((sum, item) => sum + item.rowCount, 0);
    return [
        head,
        {
            key: "__other__",
            label: "Other",
            netValue: otherNet,
            rowCount: otherRows,
            chartValue: otherChart,
            isNegative: otherNet < 0
        }
    ];
}

function creditQualityLabel(key: string): string {
    if (key === "__not_reported__" || key === "__blank__") {
        return "Not reported";
    }
    const step = Number(key);
    if (Number.isFinite(step)) {
        return `Step ${step}`;
    }
    return `Step ${key}`;
}

interface PreparedRow extends TptRow {
    marketValue: number;
    cicBucket: ReturnType<typeof cicBucket>;
}

function prepareFrame(rows: TptRow[]): PreparedRow[] {
    return rows.map((row) => ({
        ...row,
        marketValue: toFloat(row[MARKET_VALUE_KEY]),
        cicBucket: cicBucket(row[CIC_COLUMN])
    }));
}

function portfolioCurrencyInfo(frame: PreparedRow[]): [string, boolean, string[]] {
    const currencies = Array.from(
        new Set(frame.map((row) => normalizeKey(row[PORTFOLIO_CURRENCY_COLUMN])).filter(Boolean))
    ).sort();
    if (!currencies.length) {
        return ["", true, []];
    }
    if (currencies.length === 1) {
        return [currencies[0], false, currencies];
    }
    return [currencies[0], true, currencies];
}

function buildCicBucketDimension(frame: PreparedRow[]): FundParseExposureDimensionSummary {
    const lookthrough: FundParseExposureSlice[] = [];
    for (const bucket of ["bond", "equity", "cash"] as const) {
        const bucketRows = frame.filter((row) => row.cicBucket === bucket);
        lookthrough.push(
            buildSlice(bucket, CIC_BUCKET_LABELS[bucket], bucketRows.reduce((sum, row) => sum + row.marketValue, 0), bucketRows.length)
        );
    }

    const excludedRows = frame.filter((row) => EXCLUDED_BUCKETS.has(row.cicBucket));
    const excludedNet = excludedRows.reduce((sum, row) => sum + row.marketValue, 0);
    const excludedGross = excludedRows.reduce((sum, row) => sum + Math.abs(row.marketValue), 0);

    return {
        scope: "lookthrough_with_excluded_panel",
        lookthrough,
        excluded: {
            key: "derivative_and_other",
            label: "Derivatives & other",
            netValue: excludedNet,
            grossAbsValue: excludedGross,
            rowCount: excludedRows.length,
            includesNegativeNet: excludedNet < 0 || excludedRows.some((row) => row.marketValue < 0)
        }
    };
}

function buildLookthroughFrame(frame: PreparedRow[]): PreparedRow[] {
    return frame.filter((row) => LOOKTHROUGH_BUCKETS.has(row.cicBucket));
}

function buildBondFrame(frame: PreparedRow[]): PreparedRow[] {
    return frame.filter((row) => row.cicBucket === "bond");
}

export function buildExposureChartsSummary(rows: TptRow[], valuationDate: string): FundParseExposureChartsSummary {
    const frame = prepareFrame(rows);
    const [reportingCurrency, multiPortfolioCurrency, portfolioCurrencies] = portfolioCurrencyInfo(frame);
    const lookthroughFrame = buildLookthroughFrame(frame);
    const excludedFrame = frame.filter((row) => EXCLUDED_BUCKETS.has(row.cicBucket));
    const issuerCountrySlices = aggregateByKey(lookthroughFrame, ISSUER_COUNTRY_COLUMN);
    const [issuerCountryCollapsed, issuerCountryOther] = collapseTopN(issuerCountrySlices, COUNTRY_TOP_N);
    const quotationCurrencySlices = aggregateByKey(lookthroughFrame, QUOTATION_CURRENCY_COLUMN);
    const [quotationCurrencyCollapsed, quotationCurrencyOther] = collapseTopN(quotationCurrencySlices, COUNTRY_TOP_N);

    const bonds = buildBondFrame(frame);
    const bondStepRows = bonds.map((row) => ({
        ...row,
        _credit_step_key: normalizeKey(row[CREDIT_QUALITY_STEP_COLUMN]) || "__not_reported__"
    }));
    const bondCreditSlices = aggregateByKey(bondStepRows, "_credit_step_key", creditQualityLabel).sort((left, right) => {
        if (left.key === "__not_reported__") {
            return 1;
        }
        if (right.key === "__not_reported__") {
            return -1;
        }
        return left.key.localeCompare(right.key);
    });
    const bondRowsWithStep = bonds.filter((row) => normalizeKey(row[CREDIT_QUALITY_STEP_COLUMN])).length;

    return {
        valuationDate,
        measureColumn: MARKET_VALUE_KEY,
        reportingCurrency,
        multiPortfolioCurrency,
        portfolioCurrencies,
        dimensions: {
            cic_bucket: buildCicBucketDimension(frame),
            issuer_country: {
                scope: "lookthrough_only",
                slices: issuerCountryCollapsed,
                topN: COUNTRY_TOP_N,
                otherSlice: issuerCountryOther
            },
            quotation_currency: {
                scope: "lookthrough_only",
                slices: quotationCurrencyCollapsed,
                topN: COUNTRY_TOP_N,
                otherSlice: quotationCurrencyOther
            },
            bond_credit_quality_step: {
                scope: "bond_only",
                slices: bondCreditSlices,
                bondRowCount: bonds.length,
                bondRowsWithStep
            }
        },
        totals: {
            allRowsNetValue: frame.reduce((sum, row) => sum + row.marketValue, 0),
            lookthroughNetValue: lookthroughFrame.reduce((sum, row) => sum + row.marketValue, 0),
            excludedNetValue: excludedFrame.reduce((sum, row) => sum + row.marketValue, 0),
            excludedGrossAbsValue: excludedFrame.reduce((sum, row) => sum + Math.abs(row.marketValue), 0),
            negativeRowCount: frame.filter((row) => row.marketValue < 0).length,
            rowCount: frame.length
        }
    };
}

export type { FundParseExposureDimensionKey };
